import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { container, gutters } from "@/components/ds";
import { cn } from "@/lib/utils";

/**
 * The homepage card for a signed-in visitor who has uploaded bloodwork: their latest grade, the biological age
 * estimate, the three best healthspan scores and the upload date, linking to /tools/bloodwork. Renders nothing for
 * anyone else, so it costs anonymous visitors nothing.
 *
 * Restyled 2026-10-22 on the design system: status colours with the grade's word beside them, no text under 12px,
 * and words instead of dashes for missing values.
 */
const GRADE: Record<string, { word: string; tone: string }> = {
  A: { word: "Excellent", tone: "border-success/40 bg-success/10 text-success" },
  B: { word: "Good", tone: "border-success/40 bg-success/10 text-success" },
  C: { word: "Fair", tone: "border-warning/40 bg-warning/10 text-warning" },
  D: { word: "Needs work", tone: "border-warning/40 bg-warning/10 text-warning" },
  F: { word: "Critical", tone: "border-destructive/40 bg-destructive/10 text-destructive" },
};

export default function BloodworkDashboardWidget() {
  const { user } = useAuth();
  const [latest, setLatest] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    supabase
      .from("bloodwork_uploads")
      .select("id, overall_grade, biological_age_estimate, upload_date, created_at, healthspan_scores, lab_name")
      .eq("user_id", user.id)
      .eq("is_valid_bloodwork", true)
      .not("overall_grade", "is", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setLatest(data);
        setLoading(false);
      });
  }, [user]);

  // Nothing for signed-out visitors, while loading, or before a first analysis.
  if (!user || loading || !latest) return null;

  const grade: string | null = latest.overall_grade || null;
  const g = grade ? GRADE[grade] : undefined;
  const bioAge = latest.biological_age_estimate;
  const date = latest.upload_date || latest.created_at;
  const scores = (latest.healthspan_scores || {}) as Record<string, number>;
  const topScores = Object.entries(scores)
    .sort(([, a], [, b]) => (b as number) - (a as number))
    .slice(0, 3);

  return (
    <section aria-label="Your latest bloodwork" className={cn(container.default, gutters, "pb-2")}>
      <Link
        to="/tools/bloodwork"
        className="group block rounded-xl border border-border bg-card p-4 transition-colors duration-150 hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-5"
      >
        <div className="flex items-center justify-between gap-3">
          <p className="type-eyebrow text-muted-foreground">Your latest bloodwork</p>
          <span className="type-small inline-flex items-center gap-1 font-medium text-primary group-hover:underline group-hover:underline-offset-4">
            Open <ArrowRight aria-hidden className="h-4 w-4" />
          </span>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-4">
          <div className="flex items-center gap-3">
            <span
              className={cn(
                "type-h2 flex h-14 w-14 items-center justify-center rounded-xl border font-semibold",
                g ? g.tone : "border-border bg-secondary text-muted-foreground",
              )}
            >
              {grade ?? "?"}
            </span>
            <span>
              <span className="type-small block text-muted-foreground">Overall grade</span>
              <span className="type-body block font-semibold text-foreground">{g ? g.word : "Not graded"}</span>
            </span>
          </div>
          {bioAge && (
            <div>
              <span className="type-small block text-muted-foreground">Biological age estimate</span>
              <span className="type-h2 block font-semibold tabular-nums text-foreground">{bioAge}</span>
            </div>
          )}
          {topScores.length > 0 && (
            <dl className="hidden items-center gap-6 sm:flex">
              {topScores.map(([key, val]) => (
                <div key={key} className="flex flex-col-reverse">
                  <dt className="type-small capitalize text-muted-foreground">{key.replace(/_/g, " ")}</dt>
                  <dd className="type-body font-semibold tabular-nums text-foreground">{val as number}</dd>
                </div>
              ))}
            </dl>
          )}
          <p className="type-small ml-auto tabular-nums text-muted-foreground">
            {date ? `Uploaded ${format(new Date(date), "MMM d, yyyy")}` : "No upload date"}
          </p>
        </div>
      </Link>
    </section>
  );
}
